import errorHandler from './utils/errorHandler';
import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionsContext';

const ProtectedRoute = ({ children, allowedRoles = [], requiredPermission = null }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { hasPermission, isOwner, loading: permLoading } = usePermissions();
  const location = useLocation();

  // Show loading while checking auth/permissions
  if (authLoading || permLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f8fafc',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#0d9488',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p style={{ color: '#64748b' }}>Loading...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); }}`}</style>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    errorHandler.logDebug('ProtectedRoute', 'Not authenticated, redirecting to login');
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check role-based access (if roles specified)
  if (allowedRoles.length > 0) {
    const userRole = user.role?.toUpperCase() || '';
    const hasRole = allowedRoles.some(role => role.toUpperCase() === userRole);

    if (!hasRole) {
      errorHandler.logDebug('ProtectedRoute', 'User not authorized for this route');
      // This is a role mismatch on a perfectly valid session — not an
      // expired-session case. Pass that explicitly, plus the user's actual
      // role, so /unauthorized shows the right message and sends them back
      // to their own dashboard rather than auto-bouncing through /login.
      return (
        <Navigate
          to="/unauthorized"
          state={{ reason: 'access_denied', userRole: user.role }}
          replace
        />
      );
    }
  }

  // Check permission-based access (if permission specified)
  // Owners always pass, managers need the specific permission
  // SOLO users bypass the permission check — they own everything in their account
  const isSoloUser = user.role?.toUpperCase() === 'SOLO';
  if (requiredPermission && !isOwner() && !isSoloUser) {
    if (!hasPermission(requiredPermission)) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: '#f8fafc',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}>
          <div style={{
            textAlign: 'center',
            padding: '40px',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            maxWidth: '400px',
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h2 style={{ color: '#1e293b', margin: '0 0 8px 0' }}>Access Restricted</h2>
            <p style={{ color: '#64748b', margin: '0 0 20px 0' }}>
              You don't have permission to access this section.
              Contact your account owner to request access.
            </p>
            <button
              onClick={() => window.history.back()}
              style={{
                background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  // All checks passed - render children or Outlet
  return children ? children : <Outlet />;
};

export default ProtectedRoute;